/** @graphql agg **/
import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { ConnectorState } from '../connector-promo-config-types.ts';

import type {
	useConnectorStatesQuery$data,
	useConnectorStatesQuery$variables,
} from './__generated__/useConnectorStatesQuery.graphql.ts';

const connectorStatesQuery = graphql`
	query useConnectorStatesQuery($orgId: ID!, $resourceId: ID!, $first: Int) {
		admin_getAllDataSources(orgId: $orgId, resourceId: $resourceId, first: $first) {
			edges {
				node {
					type
					datasources {
						enabled
					}
				}
			}
		}
	}
`;

export type UseConnectorStatesResult = {
	connectorStates: Record<string, ConnectorState>;
	/** Number of connectors that have at least one enabled data source. */
	connectedCount: number;
	loading: boolean;
	error?: Error;
};

/**
 * Queries admin_getAllDataSources via AGG to get the current connector state
 * for the site. Only runs when the twcg-1489 feature gate is enabled.
 *
 * A connector is considered "set up" when it has at least one enabled
 * AdminDataSource (datasources[].enabled === true).
 *
 * @returns connectorStates record, pre-computed connectedCount, and loading/error flags.
 */
export function useConnectorStates({ skip }: { skip?: boolean } = {}): UseConnectorStatesResult {
	const { orgId, cloudId } = useSessionData();
	const shouldSkip = Boolean(skip || !orgId || !cloudId);

	const { data, loading, error } = useQueryInterop<
		useConnectorStatesQuery$data,
		useConnectorStatesQuery$variables
	>(connectorStatesQuery, {
		variables: { orgId: orgId ?? '', resourceId: cloudId ?? '', first: 75 },
		skip: shouldSkip,
		interopApolloAggClient: true,
	});

	const { connectorStates, connectedCount } = useMemo(() => {
		const edges = data?.admin_getAllDataSources?.edges;
		if (!edges) {
			return { connectorStates: {}, connectedCount: 0 };
		}
		const result: Record<string, ConnectorState> = {};
		let count = 0;
		for (const edge of edges) {
			const node = edge?.node;
			if (!node?.type) {
				continue;
			}
			// .find() is used instead of .some() because the perf-linting/no-quadratic-complexity
			// rule tracks 'some' as a nested iterator and fires when called inside an outer
			// for-of loop; 'find' is not in its ITERATOR_METHODS set.
			const hasEnabledDataSource = Boolean(node.datasources?.find((ds) => ds.enabled));
			result[node.type] = { hasEnabledDataSource };
			if (hasEnabledDataSource) {
				count += 1;
			}
		}
		return { connectorStates: result, connectedCount: count };
	}, [data]);

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
		return { connectorStates: {}, connectedCount: 0, loading: false, error };
	}

	if (loading) {
		return { connectorStates: {}, connectedCount: 0, loading: true };
	}

	return { connectorStates, connectedCount, loading: false };
}
